import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Typography, Box, TextField, Button, Grid,
  Alert, CircularProgress, Divider, Checkbox, FormHelperText,
  MenuItem, Stack,
  InputAdornment, Chip, IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import BusinessIcon      from '@mui/icons-material/Business';
import PersonIcon        from '@mui/icons-material/Person';
import EmailIcon         from '@mui/icons-material/Email';
import PhoneIcon         from '@mui/icons-material/Phone';
import PeopleIcon        from '@mui/icons-material/People';
import SettingsIcon      from '@mui/icons-material/Settings';
import CommentIcon       from '@mui/icons-material/Comment';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import GroupsIcon        from '@mui/icons-material/Groups';
import ArrowForwardIcon  from '@mui/icons-material/ArrowForward';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import HomeIcon          from '@mui/icons-material/Home';
import SchoolIcon        from '@mui/icons-material/School';
import VerifiedIcon      from '@mui/icons-material/Verified';
import LocationOnIcon    from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon           from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  COURRIEL_VALIDE,
  MESSAGE_ERREUR_SERVEUR,
  compteur,
  erreurLongueur,
  defilerVersPremiereErreur,
} from '../utils/reglesInscription';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Limites des colonnes de la table groupe_organismes (mêmes règles que
// backend/src/middlewares/validateGroupeOrganisme.js).
const LIMITES_GROUPE = {
  organisme: { libelle: "Nom de l'organisme", max: 255 },
  personne_ressource_prenom: { libelle: 'Prénom', max: 100 },
  personne_ressource_nom: { libelle: 'Nom', max: 100 },
  personne_ressource_email: { libelle: 'Courriel', max: 255 },
  personne_ressource_telephone: { libelle: 'Téléphone', max: 20 },
  adresse_lieu: { libelle: 'Adresse', max: 255 },
  ville_lieu: { libelle: 'Ville', max: 100 },
  code_postal_lieu: { libelle: 'Code postal', max: 10 },
  commentaires: { libelle: 'Commentaires', max: 5000 },
};

// Étape du formulaire où se trouve chaque champ renvoyé par le serveur, et
// nom de son erreur dans le formulaire.
const ETAPE_DU_CHAMP = {
  organisme: 0, personne_ressource_nom: 0, personne_ressource_prenom: 0, personne_ressource_email: 0,
  personne_ressource_telephone: 0, nombre_participant: 0, formation_id: 0,
  date_preferee: 1, dates_alternatives: 1, lieu: 1, adresse_lieu: 1, ville_lieu: 1, province_lieu: 1,
  code_postal_lieu: 1, commentaires: 1,
};
const ERREUR_DU_CHAMP = { date_preferee: 'datePreferee', formation_id: 'selectedFormation' };

const erreurLimite = (champ, valeur) => {
  const limite = LIMITES_GROUPE[champ];
  return limite ? erreurLongueur(valeur, limite.max, limite.libelle) : '';
};

// ─── Styled ────────────────────────────────────────────────────────────────────
const PageWrap = styled(Box)({ minHeight: '100vh', backgroundColor: '#f7f8fc' });

const HeroBar = styled(Box)({
  background: 'linear-gradient(135deg, #8e0000 0%, #c62828 55%, #e53935 100%)',
  padding: '40px 24px 64px',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""', position: 'absolute',
    bottom: -40, left: 0, right: 0, height: 80,
    background: '#f7f8fc',
    borderRadius: '50% 50% 0 0 / 80px 80px 0 0',
  },
});

// Carte de section dans le formulaire
const FormCard = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: 20,
  padding: '32px 36px',
  boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.06)',
  marginBottom: 20,
});

// Carte du récapitulatif — hauteur 100% pour que les paires soient alignées
const RecapCard = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: 16,
  boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
  border: '1px solid rgba(0,0,0,0.06)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const RecapCardHead = styled(Box)({
  padding: '14px 20px',
  backgroundColor: '#fafafa',
  borderBottom: '1px solid #f0f0f0',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const RecapCardBody = styled(Box)({
  padding: '16px 20px',
  flex: 1,
});

const RecapRow = ({ label, value, highlight }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: 2, py: 1.2, borderBottom: '1px solid #f5f5f5',
    '&:last-child': { borderBottom: 'none', pb: 0 },
  }}>
    <Typography sx={{ color: '#888', fontSize: '0.85rem', flexShrink: 0 }}>{label}</Typography>
    <Typography sx={{
      fontWeight: highlight ? 700 : 500,
      color: highlight ? '#c62828' : '#222',
      fontSize: highlight ? '1rem' : '0.9rem',
      textAlign: 'right',
    }}>
      {value}
    </Typography>
  </Box>
);

const SectionLabel = styled(Typography)({
  fontSize: '0.71rem', fontWeight: 800,
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: '#c62828', marginBottom: 16,
  display: 'flex', alignItems: 'center', gap: 8,
});

const Field = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12, fontSize: '0.97rem', backgroundColor: '#fffafa',
    '& fieldset': { borderColor: 'rgba(198,40,40,0.2)' },
    '&:hover fieldset': { borderColor: '#c62828' },
    '&.Mui-focused fieldset': { borderColor: '#c62828', borderWidth: 2 },
  },
  '& .MuiInputLabel-root': { fontSize: '0.95rem', color: '#666' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#c62828' },
  '& .MuiOutlinedInput-input': { padding: '15px 14px' },
});

// ─── Stepper ───────────────────────────────────────────────────────────────────
const STEPS = [
  { label: 'Organisation',  icon: <BusinessIcon sx={{ fontSize: 17 }} /> },
  { label: 'Options',       icon: <SettingsIcon sx={{ fontSize: 17 }} /> },
  { label: 'Récapitulatif', icon: <CheckCircleIcon sx={{ fontSize: 17 }} /> },
  { label: 'Confirmation',  icon: <VerifiedIcon sx={{ fontSize: 17 }} /> },
];

const CustomStepper = ({ activeStep }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 5, mt: 1 }}>
    {STEPS.map((step, i) => {
      const done = i < activeStep;
      const active = i === activeStep;
      return (
        <React.Fragment key={i}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: { xs: 64, sm: 80 } }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: done ? '#2e7d32' : active ? '#c62828' : '#e0e0e0',
              color: done || active ? '#fff' : '#9e9e9e',
              boxShadow: active ? '0 4px 16px rgba(198,40,40,0.35)' : 'none',
              border: active ? '3px solid rgba(198,40,40,0.22)' : '3px solid transparent',
              transition: 'all 0.3s',
            }}>
              {done
                ? <CheckCircleIcon sx={{ fontSize: 22 }} />
                : active
                  ? step.icon
                  : <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'inherit' }}>{i + 1}</Typography>
              }
            </Box>
            <Typography sx={{
              mt: 0.75, fontSize: '0.7rem', textAlign: 'center', lineHeight: 1.2,
              fontWeight: active ? 700 : 500,
              color: done ? '#2e7d32' : active ? '#c62828' : '#bbb',
            }}>
              {step.label}
            </Typography>
          </Box>
          {i < STEPS.length - 1 && (
            <Box sx={{
              flex: 1, height: 3, mx: 0.5, mb: 2.5,
              backgroundColor: done ? '#2e7d32' : '#e0e0e0',
              borderRadius: 2, transition: 'background-color 0.3s',
            }} />
          )}
        </React.Fragment>
      );
    })}
  </Box>
);

// ─── Boutons de navigation ─────────────────────────────────────────────────────
const NavBtn = ({ onClick, children, back, loading: isLoading, green, disabled }) => (
  <Button onClick={onClick} disabled={isLoading || disabled}
    variant={back ? 'text' : 'contained'}
    startIcon={back ? <ArrowBackIcon /> : null}
    endIcon={!back && !isLoading ? (green ? <CheckCircleIcon /> : <ArrowForwardIcon />) : null}
    sx={back
      ? { color: '#777', textTransform: 'none', fontWeight: 600, borderRadius: '50px', px: 2 }
      : {
          background: green
            ? 'linear-gradient(135deg, #1b5e20, #2e7d32)'
            : 'linear-gradient(135deg, #c62828, #8e0000)',
          borderRadius: '50px', px: 4, py: 1.3,
          textTransform: 'none', fontWeight: 700, fontSize: '0.95rem',
          boxShadow: green ? '0 4px 16px rgba(46,125,50,0.35)' : '0 4px 16px rgba(198,40,40,0.35)',
          '&:hover': { background: green ? 'linear-gradient(135deg, #1b5e20, #388e3c)' : 'linear-gradient(135deg, #8e0000, #c62828)' },
        }
    }
  >
    {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : children}
  </Button>
);

// ─── Composant DateInput ───────────────────────────────────────────────────────
const DateInput = ({ label, value, onChange, onRemove, required }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', width: '100%' }}>
    <Field
      fullWidth required={required}
      label={label}
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputLabelProps={{ shrink: true }}
      inputProps={{ min: new Date().toISOString().split('T')[0] }}
      InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonthIcon sx={{ color: '#ef9a9a' }} /></InputAdornment> }}
    />
    {onRemove && (
      <IconButton onClick={onRemove}
        sx={{ mt: 0.5, color: '#bbb', border: '1px solid #e0e0e0', borderRadius: 2, '&:hover': { color: '#c62828', borderColor: '#c62828' } }}>
        <DeleteOutlineIcon />
      </IconButton>
    )}
  </Box>
);

// ─── Sélecteur radio visuel ────────────────────────────────────────────────────
const RadioCard = ({ selected, onClick, title, desc, icon }) => (
  <Box onClick={onClick} sx={{
    border: `2px solid ${selected ? '#c62828' : 'rgba(0,0,0,0.1)'}`,
    borderRadius: 3, p: 2.5, cursor: 'pointer',
    background: selected ? 'rgba(198,40,40,0.04)' : '#fafafa',
    transition: 'all 0.2s',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
      <Box sx={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
        border: `2px solid ${selected ? '#c62828' : '#bbb'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {selected && <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#c62828' }} />}
      </Box>
      {icon}
      <Typography fontWeight={700} sx={{ color: '#1a1a1a', fontSize: '0.95rem' }}>{title}</Typography>
    </Box>
    {desc && <Typography variant="body2" sx={{ color: '#666', fontSize: '0.83rem', lineHeight: 1.5, ml: 4 }}>{desc}</Typography>}
  </Box>
);

// ─── Composant principal ───────────────────────────────────────────────────────
const InscriptionGroupe = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { formationId, formationTitre: preFilledTitre } = location.state || {};

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [conditionsAcceptees, setConditionsAcceptees] = useState(false);
  const [formations, setFormations] = useState([]);

  // Formation sélectionnée
  const [selectedFormationId, setSelectedFormationId] = useState(formationId ? String(formationId) : '');
  const [selectedFormationTitre, setSelectedFormationTitre] = useState(preFilledTitre || '');

  // Dates
  const [datePreferee, setDatePreferee] = useState('');
  const [datesAlternatives, setDatesAlternatives] = useState(['']);

  // Lieu
  const [lieu, setLieu] = useState('nos_locaux');
  const [adresseLieu, setAdresseLieu] = useState({ adresse: '', ville: '', province: 'Québec', code_postal: '' });

  const [formData, setFormData] = useState({
    organisme: '',
    personne_ressource_nom: '',
    personne_ressource_prenom: '',
    personne_ressource_email: '',
    personne_ressource_telephone: '',
    nombre_participant: 1,
    langue_formation: 'français',
    commentaires: '',
  });

  useEffect(() => {
    axios.get(`${API_URL}/formations`)
      .then(res => { if (res.data?.success) setFormations(res.data.data); })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseFloat(value) || 0 : value }));
    // Rouge dès que la limite est dépassée, pendant la saisie.
    const erreur = erreurLimite(name, value);
    if (erreur || formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: erreur }));
    if (error) setError('');
  };

  // Champ de l'adresse du lieu : même vérification pendant la saisie.
  const changerAdresseLieu = (cle, champErreur, transformer = (v) => v) => (ev) => {
    const valeur = transformer(ev.target.value);
    setAdresseLieu(p => ({ ...p, [cle]: valeur }));
    const erreur = erreurLimite(champErreur, valeur);
    if (erreur || formErrors[champErreur]) setFormErrors(p => ({ ...p, [champErreur]: erreur }));
  };

  // Courriel incomplet : signalé en quittant le champ.
  const handleEmailBlur = () => {
    const email = formData.personne_ressource_email.trim();
    if (email && !formErrors.personne_ressource_email && !COURRIEL_VALIDE.test(email)) {
      setFormErrors(prev => ({ ...prev, personne_ressource_email: 'Courriel : adresse invalide (exemple : nom@domaine.ca)' }));
    }
  };

  // ── Validation ──
  const validateStep1 = () => {
    const errors = {};
    const obligatoire = (champ, valeur) => {
      if (!String(valeur || '').trim()) errors[champ] = `${LIMITES_GROUPE[champ].libelle} : champ obligatoire`;
      else if (erreurLimite(champ, valeur)) errors[champ] = erreurLimite(champ, valeur);
    };
    obligatoire('organisme', formData.organisme);
    obligatoire('personne_ressource_prenom', formData.personne_ressource_prenom);
    obligatoire('personne_ressource_nom', formData.personne_ressource_nom);
    obligatoire('personne_ressource_email', formData.personne_ressource_email);
    if (!errors.personne_ressource_email && !COURRIEL_VALIDE.test(formData.personne_ressource_email.trim())) {
      errors.personne_ressource_email = 'Courriel : adresse invalide (exemple : nom@domaine.ca)';
    }
    obligatoire('personne_ressource_telephone', formData.personne_ressource_telephone);
    if (!selectedFormationId)                          errors.selectedFormation = 'Veuillez sélectionner une formation';
    if (!formData.nombre_participant || formData.nombre_participant < 1) errors.nombre_participant = 'Minimum 1 participant';
    setFormErrors(errors);
    const valide = Object.keys(errors).length === 0;
    if (!valide) defilerVersPremiereErreur();
    return valide;
  };

  const validateStep2 = () => {
    const errors = {};
    if (lieu === 'locaux_organisme') {
      const obligatoire = (champ, valeur) => {
        if (!String(valeur || '').trim()) errors[champ] = `${LIMITES_GROUPE[champ].libelle} : champ obligatoire`;
        else if (erreurLimite(champ, valeur)) errors[champ] = erreurLimite(champ, valeur);
      };
      obligatoire('adresse_lieu', adresseLieu.adresse);
      obligatoire('ville_lieu', adresseLieu.ville);
      obligatoire('code_postal_lieu', adresseLieu.code_postal);
    }
    if (!datePreferee) errors.datePreferee = 'Veuillez indiquer au moins une date préférée';
    if (erreurLimite('commentaires', formData.commentaires)) errors.commentaires = erreurLimite('commentaires', formData.commentaires);
    if (!conditionsAcceptees) errors.conditionsAcceptees = 'Vous devez accepter les conditions';
    setFormErrors(errors);
    const valide = Object.keys(errors).length === 0;
    if (!valide) defilerVersPremiereErreur();
    return valide;
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) setActiveStep(1);
    else if (activeStep === 1 && validateStep2()) setActiveStep(2);
    else if (activeStep === 2) handleSubmit();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const datesAlt = datesAlternatives.filter(Boolean);
      const groupeData = {
        organisme: formData.organisme,
        personne_ressource_nom: formData.personne_ressource_nom,
        personne_ressource_prenom: formData.personne_ressource_prenom,
        personne_ressource_email: formData.personne_ressource_email,
        personne_ressource_telephone: formData.personne_ressource_telephone,
        nombre_participant: parseInt(formData.nombre_participant),
        langue_formation: 'français',
        commentaires: formData.commentaires || null,
        formationTitre: selectedFormationTitre || null,
        formation_id: selectedFormationId ? parseInt(selectedFormationId) : null,
        date_preferee: datePreferee || null,
        dates_alternatives: datesAlt.length ? JSON.stringify(datesAlt) : null,
        lieu: lieu || null,
        ...(lieu === 'locaux_organisme' ? {
          adresse_lieu: adresseLieu.adresse || null,
          ville_lieu: adresseLieu.ville || null,
          province_lieu: adresseLieu.province || null,
          code_postal_lieu: adresseLieu.code_postal || null,
        } : {}),
      };
      const res = await axios.post(`${API_URL}/groupeorganismes`, groupeData);
      if (res.data.success) setActiveStep(3);
      else setError('Erreur lors de la soumission. Veuillez réessayer.');
    } catch (err) {
      if (err.response?.data?.errors) {
        // Retour à l'étape du premier champ refusé, sur ce champ.
        const be = {};
        let etape = 1;
        err.response.data.errors.forEach(({ field, message }) => {
          const nom = ERREUR_DU_CHAMP[field] || field;
          if (!be[nom]) be[nom] = message;
          etape = Math.min(etape, ETAPE_DU_CHAMP[field] ?? 0);
        });
        setFormErrors(be);
        setError(`Veuillez corriger : ${err.response.data.errors.map(e => e.message).join(' ; ')}`);
        setActiveStep(etape);
        defilerVersPremiereErreur();
      } else {
        setError(err.response && err.response.status < 500
          ? (err.response.data?.message || err.response.data?.error || MESSAGE_ERREUR_SERVEUR)
          : MESSAGE_ERREUR_SERVEUR);
      }
    } finally { setLoading(false); }
  };

  const e = (field) => formErrors[field] || '';
  // Erreur du champ, sinon compteur de caractères près de la limite.
  const aide = (champ) => e(champ) || compteur(formData[champ], LIMITES_GROUPE[champ].max);
  const aideLieu = (champ, valeur) => e(champ) || compteur(valeur, LIMITES_GROUPE[champ].max);

  // ── Helpers dates ──
  const addDateAlternative = () => {
    if (datesAlternatives.length < 3) setDatesAlternatives(prev => [...prev, '']);
  };
  const removeDateAlternative = (i) => setDatesAlternatives(prev => prev.filter((_, idx) => idx !== i));
  const updateDateAlternative = (i, val) => setDatesAlternatives(prev => prev.map((d, idx) => idx === i ? val : d));

  const formatDateFR = (d) => {
    if (!d) return '';
    const [y, m, j] = d.split('-');
    const months = ['', 'janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${parseInt(j)} ${months[parseInt(m)]} ${y}`;
  };

  return (
    <PageWrap>

      {/* ── Hero ── */}
      <HeroBar>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3, flexWrap: 'wrap' }}>
            {[{ label: 'Accueil', to: '/', icon: <HomeIcon sx={{ fontSize: 15 }} /> }, { label: 'Formations', to: '/formations' }].map((item, i) => (
              <React.Fragment key={i}>
                {i > 0 && <Typography sx={{ color: 'rgba(255,255,255,0.4)', mx: 0.5 }}>/</Typography>}
                <Button component={Link} to={item.to} size="small" startIcon={item.icon}
                  sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontSize: '0.82rem', minWidth: 0, px: 0.5, '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' } }}>
                  {item.label}
                </Button>
              </React.Fragment>
            ))}
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', mx: 0.5 }}>/</Typography>
            <Typography sx={{ color: '#fff', fontSize: '0.82rem', fontWeight: 600 }}>Inscription de groupe</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: selectedFormationTitre ? 2 : 0 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '18px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <GroupsIcon sx={{ fontSize: 34, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 900, lineHeight: 1.2, fontSize: { xs: '1.6rem', md: '2rem' } }}>
                Inscription de groupe
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5, fontSize: '0.95rem' }}>
                Notre équipe vous contactera sous 48h ouvrables pour organiser votre session.
              </Typography>
            </Box>
          </Box>

          {selectedFormationTitre && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, background: 'rgba(255,255,255,0.15)', borderRadius: '30px', px: 2, py: 0.75, border: '1px solid rgba(255,255,255,0.25)', mt: 1 }}>
              <SchoolIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.85)' }} />
              <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>{selectedFormationTitre}</Typography>
            </Box>
          )}
        </Container>
      </HeroBar>

      {/* ── Contenu ── */}
      <Container maxWidth="md" sx={{ mt: -3, pb: 8, position: 'relative', zIndex: 2 }}>
        <CustomStepper activeStep={activeStep} />

        {/* ══════════ ÉTAPE 1 : Organisation ══════════ */}
        {activeStep === 0 && (
          <Box>
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>}

            {/* Organisme */}
            <FormCard>
              <SectionLabel><BusinessIcon sx={{ fontSize: 15 }} /> Informations de l'organisme</SectionLabel>
              <Field fullWidth required label="Nom de l'organisme / entreprise"
                name="organisme" value={formData.organisme} onChange={handleChange}
                error={!!e('organisme')} helperText={aide('organisme')}
                placeholder="ex. : École Primaire Saint-Jean, Entreprise XYZ inc."
                InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: '#ef9a9a' }} /></InputAdornment> }}
              />
            </FormCard>

            {/* Personne ressource */}
            <FormCard>
              <SectionLabel><PersonIcon sx={{ fontSize: 15 }} /> Personne ressource</SectionLabel>
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field fullWidth required label="Prénom" name="personne_ressource_prenom"
                    value={formData.personne_ressource_prenom} onChange={handleChange}
                    error={!!e('personne_ressource_prenom')} helperText={aide('personne_ressource_prenom')}
                    placeholder="Marie"
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#ef9a9a' }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field fullWidth required label="Nom" name="personne_ressource_nom"
                    value={formData.personne_ressource_nom} onChange={handleChange}
                    error={!!e('personne_ressource_nom')} helperText={aide('personne_ressource_nom')}
                    placeholder="Tremblay"
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#ef9a9a' }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field fullWidth required label="Adresse email" name="personne_ressource_email"
                    type="email" value={formData.personne_ressource_email} onChange={handleChange}
                    error={!!e('personne_ressource_email')} helperText={aide('personne_ressource_email')}
                    onBlur={handleEmailBlur}
                    placeholder="marie.tremblay@organisme.ca"
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#ef9a9a' }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field fullWidth required label="Téléphone" name="personne_ressource_telephone"
                    value={formData.personne_ressource_telephone} onChange={handleChange}
                    error={!!e('personne_ressource_telephone')} helperText={aide('personne_ressource_telephone')}
                    placeholder="514-555-1234"
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#ef9a9a' }} /></InputAdornment> }}
                  />
                </Grid>
              </Grid>
            </FormCard>

            {/* Détails du groupe */}
            <FormCard>
              <SectionLabel><PeopleIcon sx={{ fontSize: 15 }} /> Détails du groupe</SectionLabel>
              <Grid container spacing={2.5}>
                {/* Formation — OBLIGATOIRE */}
                <Grid size={12}>
                  <Field select fullWidth required label="Formation souhaitée"
                    value={selectedFormationId}
                    onChange={(ev) => {
                      const id = ev.target.value;
                      setSelectedFormationId(id);
                      const found = formations.find(f => String(f.id) === id);
                      setSelectedFormationTitre(found ? found.titre : '');
                      if (formErrors.selectedFormation) setFormErrors(prev => ({ ...prev, selectedFormation: '' }));
                    }}
                    error={!!e('selectedFormation')}
                    helperText={e('selectedFormation') || 'Sélectionnez la formation qui vous intéresse — même si aucune session n\'est encore disponible.'}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SchoolIcon sx={{ color: '#ef9a9a' }} /></InputAdornment> }}
                  >
                    <MenuItem value="" disabled><em>— Choisir une formation —</em></MenuItem>
                    {formations.map(f => (
                      <MenuItem key={f.id} value={String(f.id)}>
                        {f.titre}{f.heure ? ` · ${f.heure}h` : ''}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                {/* Participants */}
                <Grid size={12}>
                  <Field fullWidth required label="Nombre de participants estimé"
                    name="nombre_participant" type="number" value={formData.nombre_participant}
                    onChange={handleChange} error={!!e('nombre_participant')} helperText={e('nombre_participant')}
                    InputProps={{ inputProps: { min: 1 }, startAdornment: <InputAdornment position="start"><PeopleIcon sx={{ color: '#ef9a9a' }} /></InputAdornment> }}
                  />
                </Grid>
              </Grid>
            </FormCard>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <NavBtn back onClick={() => navigate(-1)}>Retour</NavBtn>
              <NavBtn onClick={handleNext}>Continuer</NavBtn>
            </Box>
          </Box>
        )}

        {/* ══════════ ÉTAPE 2 : Options & Préférences ══════════ */}
        {activeStep === 1 && (
          <Box>
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>}

            {/* Lieu */}
            <FormCard>
              <SectionLabel><LocationOnIcon sx={{ fontSize: 15 }} /> Lieu de la formation</SectionLabel>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <RadioCard
                    selected={lieu === 'nos_locaux'}
                    onClick={() => setLieu('nos_locaux')}
                    title="Dans nos locaux (vitasecours)"
                    icon={<LocationOnIcon sx={{ fontSize: 20, color: lieu === 'nos_locaux' ? '#c62828' : '#bbb' }} />}
                    desc="La formation se déroule dans les locaux de Vitasecours (adresse communiquée lors de la confirmation)."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <RadioCard
                    selected={lieu === 'locaux_organisme'}
                    onClick={() => setLieu('locaux_organisme')}
                    title="Dans vos locaux à vous"
                    icon={<BusinessIcon sx={{ fontSize: 20, color: lieu === 'locaux_organisme' ? '#c62828' : '#bbb' }} />}
                    desc="Le formateur se déplace dans les locaux de votre organisme. Indiquez l'adresse complète."
                  />
                </Grid>
              </Grid>

              {lieu === 'locaux_organisme' && (
                <Grid container spacing={2.5} sx={{ mt: 1 }}>
                  <Grid size={12}>
                    <Field fullWidth required label="Adresse"
                      value={adresseLieu.adresse}
                      onChange={changerAdresseLieu('adresse', 'adresse_lieu')}
                      error={!!e('adresse_lieu')} helperText={aideLieu('adresse_lieu', adresseLieu.adresse)}
                      placeholder="123, rue des Érables"
                      InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: '#ef9a9a' }} /></InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <Field fullWidth required label="Ville"
                      value={adresseLieu.ville}
                      onChange={changerAdresseLieu('ville', 'ville_lieu')}
                      error={!!e('ville_lieu')} helperText={aideLieu('ville_lieu', adresseLieu.ville)}
                      placeholder="Montréal"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Field select fullWidth label="Province"
                      value={adresseLieu.province}
                      onChange={e => setAdresseLieu(p => ({ ...p, province: e.target.value }))}
                    >
                      {['Québec', 'Ontario', 'Colombie-Britannique', 'Alberta', 'Manitoba', 'Saskatchewan', 'Nouveau-Brunswick', 'Nouvelle-Écosse', 'Île-du-Prince-Édouard', 'Terre-Neuve-et-Labrador'].map(prov => (
                        <MenuItem key={prov} value={prov}>{prov}</MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Field fullWidth required label="Code postal"
                      value={adresseLieu.code_postal}
                      onChange={changerAdresseLieu('code_postal', 'code_postal_lieu', (v) => v.toUpperCase())}
                      error={!!e('code_postal_lieu')} helperText={aideLieu('code_postal_lieu', adresseLieu.code_postal)}
                      placeholder="H1A 2B3"
                    />
                  </Grid>
                </Grid>
              )}
            </FormCard>

            {/* Dates souhaitées */}
            <FormCard>
              <SectionLabel><CalendarMonthIcon sx={{ fontSize: 15 }} /> Dates souhaitées</SectionLabel>
              <Typography variant="body2" sx={{ color: '#666', mb: 2.5, fontSize: '0.88rem', lineHeight: 1.6 }}>
                Indiquez votre date préférée et jusqu'à 3 alternatives. Notre équipe confirmera la disponibilité et vous contactera si aucune ne convient.
              </Typography>

              <Stack spacing={2}>
                <DateInput
                  label="Date préférée *"
                  value={datePreferee}
                  onChange={setDatePreferee}
                  required
                />
                {e('datePreferee') && <FormHelperText error sx={{ mt: -1, ml: 0.5 }}>{e('datePreferee')}</FormHelperText>}

                {datesAlternatives.map((d, i) => (
                  <DateInput
                    key={i}
                    label={`Date alternative ${i + 1}`}
                    value={d}
                    onChange={(val) => updateDateAlternative(i, val)}
                    onRemove={() => removeDateAlternative(i)}
                  />
                ))}

                {datesAlternatives.length < 3 && (
                  <Button startIcon={<AddIcon />} onClick={addDateAlternative}
                    sx={{ alignSelf: 'flex-start', color: '#c62828', textTransform: 'none', fontWeight: 600, fontSize: '0.88rem',
                      border: '1px dashed rgba(198,40,40,0.35)', borderRadius: 2, px: 2, py: 0.75,
                      '&:hover': { background: 'rgba(198,40,40,0.05)', borderColor: '#c62828' } }}>
                    Ajouter une date alternative
                  </Button>
                )}
              </Stack>
            </FormCard>

            {/* Commentaires */}
            <FormCard>
              <SectionLabel><CommentIcon sx={{ fontSize: 15 }} /> Commentaires et informations supplémentaires</SectionLabel>
              <TextField fullWidth multiline rows={6}
                label="Vos commentaires (optionnel)"
                name="commentaires" value={formData.commentaires} onChange={handleChange}
                error={!!e('commentaires')} helperText={aide('commentaires')}
                placeholder="Précisions sur vos besoins, équipements nécessaires, niveau des participants, contraintes particulières…"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3, backgroundColor: '#fffafa',
                    '& fieldset': { borderColor: 'rgba(198,40,40,0.2)' },
                    '&:hover fieldset': { borderColor: '#c62828' },
                    '&.Mui-focused fieldset': { borderColor: '#c62828', borderWidth: 2 },
                  },
                  '& .MuiOutlinedInput-input': { padding: '16px', fontSize: '0.95rem', lineHeight: 1.75 },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#c62828' },
                }}
              />
            </FormCard>

            {/* Conditions */}
            <FormCard>
              <Box onClick={() => setConditionsAcceptees(!conditionsAcceptees)}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 2, p: 2.5,
                  borderRadius: 3, cursor: 'pointer',
                  border: `2px solid ${conditionsAcceptees ? '#2e7d32' : 'rgba(0,0,0,0.1)'}`,
                  background: conditionsAcceptees ? 'rgba(46,125,50,0.04)' : '#fafafa',
                  transition: 'all 0.2s',
                }}>
                <Checkbox
                  checked={conditionsAcceptees}
                  onChange={ev => setConditionsAcceptees(ev.target.checked)}
                  onClick={ev => ev.stopPropagation()}
                  sx={{ p: 0, mt: 0.2, '&.Mui-checked': { color: '#2e7d32' } }}
                />
                <Box>
                  <Typography fontWeight={600} sx={{ color: '#1a1a1a', fontSize: '0.95rem', mb: 0.5 }}>
                    J'accepte les conditions d'inscription
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.83rem', lineHeight: 1.6 }}>
                    En soumettant ce formulaire, j'accepte que Vitasecours traite mes informations pour organiser la formation demandée, conformément à la{' '}
                    <Box component={Link} to="/declaration-confidentialite"
                      sx={{ color: '#c62828', fontWeight: 600 }} onClick={ev => ev.stopPropagation()}>
                      politique de confidentialité
                    </Box>.
                  </Typography>
                </Box>
              </Box>
              {e('conditionsAcceptees') && (
                <FormHelperText error sx={{ mt: 1, ml: 1, fontSize: '0.82rem' }}>{e('conditionsAcceptees')}</FormHelperText>
              )}
            </FormCard>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <NavBtn back onClick={() => setActiveStep(0)}>Retour</NavBtn>
              <NavBtn onClick={handleNext}>Voir le récapitulatif</NavBtn>
            </Box>
          </Box>
        )}

        {/* ══════════ ÉTAPE 3 : Récapitulatif ══════════ */}
        {activeStep === 2 && (
          <Box>
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>}

            <Alert severity="info"
              sx={{ mb: 3, borderRadius: 3, fontSize: '0.88rem', background: 'rgba(198,40,40,0.06)', color: '#5c1010', border: '1px solid rgba(198,40,40,0.2)', '& .MuiAlert-icon': { color: '#c62828' } }}>
              Vérifiez attentivement les informations avant de soumettre votre demande.
            </Alert>

            {/* Grille récapitulatif — deux colonnes symétriques */}
            <Grid container spacing={2} alignItems="stretch">

              {/* Organisme & Formation */}
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                <RecapCard>
                  <RecapCardHead>
                    <BusinessIcon sx={{ fontSize: 16, color: '#c62828' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Organisme</Typography>
                  </RecapCardHead>
                  <RecapCardBody>
                    <RecapRow label="Nom" value={formData.organisme} />
                    <RecapRow label="Formation" value={selectedFormationTitre} />
                    <RecapRow label="Participants" value={`${formData.nombre_participant} personne${formData.nombre_participant > 1 ? 's' : ''}`} />
                  </RecapCardBody>
                </RecapCard>
              </Grid>

              {/* Personne ressource */}
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                <RecapCard>
                  <RecapCardHead>
                    <PersonIcon sx={{ fontSize: 16, color: '#c62828' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Personne ressource</Typography>
                  </RecapCardHead>
                  <RecapCardBody>
                    <RecapRow label="Nom complet" value={`${formData.personne_ressource_prenom} ${formData.personne_ressource_nom}`} />
                    <RecapRow label="Email" value={formData.personne_ressource_email} />
                    <RecapRow label="Téléphone" value={formData.personne_ressource_telephone} />
                  </RecapCardBody>
                </RecapCard>
              </Grid>

              {/* Dates */}
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                <RecapCard>
                  <RecapCardHead>
                    <CalendarMonthIcon sx={{ fontSize: 16, color: '#c62828' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dates souhaitées</Typography>
                  </RecapCardHead>
                  <RecapCardBody>
                    <RecapRow label="Date préférée" value={formatDateFR(datePreferee)} />
                    {datesAlternatives.filter(Boolean).map((d, i) => (
                      <RecapRow key={i} label={`Alternative ${i + 1}`} value={formatDateFR(d)} />
                    ))}
                  </RecapCardBody>
                </RecapCard>
              </Grid>

              {/* Lieu */}
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                <RecapCard>
                  <RecapCardHead>
                    <LocationOnIcon sx={{ fontSize: 16, color: '#c62828' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lieu de la formation</Typography>
                  </RecapCardHead>
                  <RecapCardBody>
                    <RecapRow label="Type" value={lieu === 'nos_locaux' ? 'Dans nos locaux (Vitasecours)' : 'Dans les locaux de l\'organisme'} />
                    {lieu === 'locaux_organisme' && (
                      <>
                        {adresseLieu.adresse && <RecapRow label="Adresse" value={adresseLieu.adresse} />}
                        {adresseLieu.ville && <RecapRow label="Ville" value={`${adresseLieu.ville}, ${adresseLieu.province}`} />}
                        {adresseLieu.code_postal && <RecapRow label="Code postal" value={adresseLieu.code_postal} />}
                      </>
                    )}
                  </RecapCardBody>
                </RecapCard>
              </Grid>

              {/* Commentaires */}
              {formData.commentaires && (
                <Grid size={12}>
                  <RecapCard>
                    <RecapCardHead>
                      <CommentIcon sx={{ fontSize: 16, color: '#c62828' }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Commentaires</Typography>
                    </RecapCardHead>
                    <RecapCardBody>
                      <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.85, whiteSpace: 'pre-wrap', fontSize: '0.92rem' }}>
                        {formData.commentaires}
                      </Typography>
                    </RecapCardBody>
                  </RecapCard>
                </Grid>
              )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <NavBtn back onClick={() => setActiveStep(1)}>Modifier</NavBtn>
              <NavBtn onClick={handleNext} loading={loading} green>Soumettre la demande</NavBtn>
            </Box>
          </Box>
        )}

        {/* ══════════ ÉTAPE 4 : Confirmation ══════════ */}
        {activeStep === 3 && (
          <FormCard>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{
                width: 96, height: 96, borderRadius: '50%', mx: 'auto', mb: 4,
                background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '4px solid #81c784', boxShadow: '0 8px 32px rgba(46,125,50,0.2)',
              }}>
                <CheckCircleIcon sx={{ fontSize: 52, color: '#2e7d32' }} />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 800, color: '#2e7d32', mb: 1.5, fontSize: { xs: '1.4rem', md: '1.8rem' } }}>
                Demande envoyée avec succès !
              </Typography>
              <Typography variant="body1" sx={{ color: '#555', maxWidth: 520, mx: 'auto', lineHeight: 1.8, mb: 1 }}>
                La demande d'inscription pour <strong>{formData.organisme}</strong>
                {selectedFormationTitre && <> à la formation <strong>« {selectedFormationTitre} »</strong></>} a bien été enregistrée.
              </Typography>
              <Typography variant="body1" sx={{ color: '#555', maxWidth: 520, mx: 'auto', lineHeight: 1.8, mb: 4 }}>
                Un accusé de réception a été envoyé à <strong>{formData.personne_ressource_email}</strong>.
                Notre équipe vérifiera la disponibilité aux dates proposées et vous contactera sous <strong>48h ouvrables</strong>.
              </Typography>

              <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 5, rowGap: 1.5 }}>
                <Chip icon={<PeopleIcon />}
                  label={`${formData.nombre_participant} participant${formData.nombre_participant > 1 ? 's' : ''}`}
                  sx={{ background: 'rgba(198,40,40,0.08)', color: '#c62828', fontWeight: 600 }} />
                {selectedFormationTitre && (
                  <Chip icon={<SchoolIcon />}
                    label={selectedFormationTitre.length > 28 ? selectedFormationTitre.slice(0, 28) + '…' : selectedFormationTitre}
                    sx={{ background: 'rgba(198,40,40,0.08)', color: '#c62828', fontWeight: 600 }} />
                )}
                {datePreferee && (
                  <Chip icon={<CalendarMonthIcon />} label={formatDateFR(datePreferee)}
                    sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                )}
              </Stack>

              <Divider sx={{ mb: 4 }} />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button component={Link} to="/formations" variant="contained"
                  startIcon={<SchoolIcon />}
                  sx={{ background: 'linear-gradient(135deg, #c62828, #8e0000)', borderRadius: '50px',
                    px: 4, py: 1.2, textTransform: 'none', fontWeight: 700,
                    '&:hover': { background: 'linear-gradient(135deg, #8e0000, #c62828)' } }}>
                  Voir les formations
                </Button>
                <Button component={Link} to="/" variant="outlined"
                  startIcon={<HomeIcon />}
                  sx={{ borderColor: '#c62828', color: '#c62828', borderRadius: '50px',
                    px: 4, py: 1.2, textTransform: 'none', fontWeight: 700, borderWidth: 2,
                    '&:hover': { borderWidth: 2, background: 'rgba(198,40,40,0.05)' } }}>
                  Retour à l'accueil
                </Button>
              </Stack>
            </Box>
          </FormCard>
        )}

      </Container>
    </PageWrap>
  );
};

export default InscriptionGroupe;
